import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { action } from "@ember/object";
import { service } from "@ember/service";
import DTooltip from "discourse/float-kit/components/d-tooltip";
import { ajax } from "discourse/lib/ajax";
import { popupAjaxError } from "discourse/lib/ajax-error";
import { and } from "discourse/truth-helpers";
import DButton from "discourse/ui-kit/d-button";
import DInterpolatedTranslation from "discourse/ui-kit/d-interpolated-translation";
import DUserLink from "discourse/ui-kit/d-user-link";
import dIcon from "discourse/ui-kit/helpers/d-icon";
import { i18n } from "discourse-i18n";
import setAcceptedSolutions from "../lib/set-accepted-solutions";

export default class SolvedUnacceptAnswerButton extends Component {
  @service appEvents;
  @service siteSettings;

  @tracked saving = false;

  @action
  async unacceptAnswer() {
    const post = this.args.post;

    this.saving = true;
    try {
      await unacceptPost(post);

      this.appEvents.trigger("discourse-solved:solution-toggled", post);
    } catch (e) {
      popupAjaxError(e);
    } finally {
      this.saving = false;
    }
  }

  get answerInfo() {
    return this.args.post.topic.accepted_answers?.find(
      (a) => a.post_number === this.args.post.post_number
    );
  }

  get showAcceptedBy() {
    return !(
      !this.siteSettings.show_who_marked_solved ||
      !this.answerInfo?.accepter_username
    );
  }

  get acceptedByUsername() {
    return this.answerInfo?.accepter_username;
  }

  get acceptedByDisplayName() {
    const username = this.answerInfo?.accepter_username;
    const name = this.answerInfo?.accepter_name;
    return this.siteSettings.display_name_on_posts && name ? name : username;
  }

  <template>
    <span class="extra-buttons">
      {{#if (and @post.can_accept_answer @post.accepted_answer)}}
        {{#if this.showAcceptedBy}}
          <DTooltip @identifier="post-action-menu__solved-accepted-tooltip">
            <:trigger>
              <DButton
                class="post-action-menu__solved-accepted accepted fade-out"
                ...attributes
                @action={{this.unacceptAnswer}}
                @icon="square-check"
                @label="solved.solution"
                @title="solved.unaccept_answer"
              />
            </:trigger>
            <:content>
              <DInterpolatedTranslation
                @key="solved.marked_solved_by"
                as |Placeholder|
              >
                <Placeholder @name="user">
                  <DUserLink @username={{this.acceptedByUsername}}>
                    {{this.acceptedByDisplayName}}
                  </DUserLink>
                </Placeholder>
              </DInterpolatedTranslation>
            </:content>
          </DTooltip>
        {{else}}
          <DButton
            class="post-action-menu__solved-accepted accepted fade-out"
            ...attributes
            @action={{this.unacceptAnswer}}
            @disabled={{this.saving}}
            @icon="square-check"
            @label="solved.solution"
            @title="solved.unaccept_answer"
          />
        {{/if}}
      {{else}}
        <span
          class="accepted-text"
          title={{i18n "solved.accepted_description"}}
        >
          <span>{{dIcon "check"}}</span>
          <span class="accepted-label">
            {{i18n "solved.solution"}}
          </span>
        </span>
      {{/if}}
    </span>
  </template>
}

async function unacceptPost(post) {
  if (!post.can_accept_answer || !post.accepted_answer) {
    return;
  }

  const topic = post.topic;

  try {
    const remainingAcceptedAnswers = await ajax("/solution/unaccept", {
      type: "POST",
      data: { id: post.id },
    });

    setAcceptedSolutions(topic, remainingAcceptedAnswers);
  } catch (e) {
    popupAjaxError(e);
  }
}
